import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api from "../../services/api";

function Create() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [customers, setCustomers] = useState([]);
  const [title, setTitle] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [currency, setCurrency] = useState("");
  const [exchangeRate, setExchangeRate] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [prefilledCustomer, setPrefilledCustomer] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await api.get("customers/");
        setCustomers(res.data);

        // Check if we have a customer ID from navigation state
        if (location.state?.customerId) {
          const customerFromState = res.data.find(c => c.id === location.state.customerId);
          if (customerFromState) {
            setCustomerId(customerFromState.id);
            setPrefilledCustomer(customerFromState);
          }
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchCustomers();
  }, [location.state]);

  const handleCreate = async (e, actionType) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    if (!title || !customerId || !budget || !currency) {
      setError("Title, Customer, Budget, and Currency are required.");
      setSubmitting(false);
      return;
    }

    if (currency !== 'USD' && (!exchangeRate || exchangeRate <= 0)) {
      setError("Exchange rate is required for non-USD currencies.");
      setSubmitting(false);
      return;
    }

    try {
      const response = await api.post("projects/", {
        title,
        customer: customerId,
        description,
        budget: parseFloat(budget),
        currency,
        exchange_rate: parseFloat(exchangeRate),
        start_date: startDate || null,
        end_date: endDate || null,
      });
      
      const newProjectId = response.data.id;
      
      if (actionType === 'createAndGoToContract') {
        // Navigate to contract generation
        navigate(`/projects/contract/${newProjectId}`);
      } else {
        // Just go back to projects list
        navigate("/projects");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to add project.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-4 sm:mb-8">
        <Link
          to="/projects"
          className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
        </Link>
        <div>
          <h1 className="text-lg sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            Add Project
          </h1>
          <p className="text-gray-600 mt-1 text-xs sm:text-base">Create a new project</p>
          {prefilledCustomer && (
            <div className="mt-2 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-700 text-xs sm:text-sm">
                <strong>Customer Pre-filled:</strong> {prefilledCustomer.name}
                {prefilledCustomer.company && ` - ${prefilledCustomer.company}`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={(e) => handleCreate(e, 'createOnly')} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full border border-gray-300 rounded-lg sm:rounded-xl p-2 sm:p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                placeholder="Project title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Customer *</label>
              {prefilledCustomer ? (
                <div className="relative">
                  <input
                    type="text"
                    value={prefilledCustomer.name}
                    className="w-full border border-gray-300 rounded-lg sm:rounded-xl p-2 sm:p-3 bg-gray-50 text-sm sm:text-base"
                    disabled
                    readOnly
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPrefilledCustomer(null);
                      setCustomerId("");
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                    title="Change customer"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              ) : (
                <select
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg sm:rounded-xl p-2 sm:p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                  required
                >
                  <option value="">Select Customer</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} {c.company ? `- ${c.company}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Budget *</label>
              <input
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="w-full border border-gray-300 rounded-lg sm:rounded-xl p-2 sm:p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                placeholder="0.00"
                step="0.01"
                min="0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Currency *</label>
              <select
                value={currency}
                onChange={(e) => {
                  setCurrency(e.target.value);
                  if (e.target.value === 'USD') {
                    setExchangeRate(1);
                  }
                }}
                className="w-full border border-gray-300 rounded-lg sm:rounded-xl p-2 sm:p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                required
              >
                <option value="">Select Currency</option>
                <option value="USD">US Dollar (USD)</option>
                <option value="AFN">Afghan Afghani (AFN)</option>
                
              </select>
            </div>

            {/* Exchange Rate Field - Only show if currency is NOT USD */}
            {currency && currency !== 'AFN' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Exchange Rate *</label>
                <div className="relative">
                  <span className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">1 {currency} =</span>
                  <input
                    type="number"
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg sm:rounded-xl p-2 sm:p-3 pl-16 sm:pl-20 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                    placeholder="0.00"
                    step="0.000001"
                    min="0.000001"
                    required
                  />
                  <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">USD</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg sm:rounded-xl p-2 sm:p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg sm:rounded-xl p-2 sm:p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="3"
                className="w-full border border-gray-300 rounded-lg sm:rounded-xl p-2 sm:p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                placeholder="Project description..."
              />
            </div>
          </div>

          {/* Buttons Section - Mobile Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
            <Link
              to="/projects"
              className="w-full sm:w-auto px-3 sm:px-6 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium text-center text-sm sm:text-base"
            >
              Cancel
            </Link>
            
            {/* Dual Buttons - Stack on mobile, row on larger screens */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto bg-gradient-to-r from-green-500 to-green-600 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-sm hover:shadow-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1 sm:space-x-2 text-sm sm:text-base"
              >
                {submitting ? (
                  <>
                    <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    <span>Create Project</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={(e) => handleCreate(e, 'createAndGoToContract')}
                disabled={submitting}
                className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-sm hover:shadow-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1 sm:space-x-2 text-sm sm:text-base"
              >
                {submitting ? (
                  <>
                    <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <span>📄</span>
                    <span>Create & Generate Contract</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Create;